/**
 * Remembering which account the portal is working in, across page loads.
 *
 * The selection used to live in React state alone, so a reload — or a
 * bookmarked `/requests` — dropped a multi-account user back onto the chooser
 * every time. That is not a security property; it was just forgetfulness, and
 * it made deep links unusable for exactly the people who have more than one
 * account to keep straight.
 *
 * **What is stored is a hint, and only a hint.** A customer id is not a secret:
 * it appears in every URL the portal calls and in every response it renders.
 * Nothing here is trusted. On restore the id is checked against the memberships
 * the *server* returned for the authenticated session — which only ever
 * contains active ones — and anything not in that list is discarded. Editing
 * this value in devtools therefore buys nothing: it either names an account you
 * already have, or it is thrown away.
 *
 * This does not weaken the rule the rest of the portal follows. The session is
 * an HttpOnly cookie, and no credential — password, API access token, client
 * secret — is written to any browser store. That stays true: what is written
 * here is an identifier the user is already looking at.
 *
 * Every access is wrapped. `localStorage` throws rather than returning null in
 * a private window, under a blocked-cookies policy, and when a quota is
 * exhausted, and a portal that cannot render because storage is disabled would
 * be a worse bug than the one this fixes.
 */

const STORAGE_KEY = 'api-platform.portal.selected-customer'

/** The chosen account id, or null when there is none we can read. */
export function readSelectedCustomer(): string | null {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY)
    return value && value.trim() ? value : null
  } catch {
    // Storage unavailable. The portal still works; it just asks again.
    return null
  }
}

export function writeSelectedCustomer(customerId: string): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, customerId)
  } catch {
    // Nothing to do and nothing worth telling the user: the selection still
    // holds for this page's lifetime, which is where it used to live anyway.
  }
}

export function clearSelectedCustomer(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY)
  } catch {
    /* see readSelectedCustomer */
  }
}

/**
 * The stored id, but only if the server still says this user may act for it.
 *
 * The whole validation rule, in one place, so no caller can restore a
 * selection without performing it. `memberships` comes from the session
 * endpoint, which returns **active** memberships only — so one that was
 * disabled or removed is simply absent from the list, and the selection naming
 * it is not valid any more.
 *
 * Pure on purpose: it neither reads nor writes storage. Deciding and erasing
 * are different jobs, and doing the second while React is rendering is how a
 * component acquires a side effect nobody expects it to have.
 */
export function validSelection(
  stored: string | null,
  memberships: readonly { customer: { id: string } }[],
): string | null {
  if (!stored) return null
  return memberships.some((row) => row.customer.id === stored) ? stored : null
}
